/**
 * QuizFilter - Product Filtering Logic using Shopify Storefront API
 * Filters products based on quiz answers using API-level filtering
 * 
 * This implementation:
 * 1. Builds combination string from quiz answers in format:
 *    - With hand_size: [hand_size],[moisture],[feel],[texture]
 *    - With glove_size: [glove_style],[glove_size],[moisture],[feel],[texture]
 * 2. Makes two parallel GraphQL queries filtering by:
 *    - quiz_combination_options metafield
 *    - quiz_combination_options_second_part metafield
 * 3. Uses the /api/2023-10/graphql.json endpoint
 * 4. Merges results from both queries and removes duplicates
 * 5. Returns only products that match the filter criteria in either field
 */

class QuizFilter {
  constructor(answers, collectionHandle = 'all') {
    this.answers = answers;
    this.collectionHandle = collectionHandle;
    
    // Storefront API endpoint
    this.apiEndpoint = '/api/2023-10/graphql.json';
    
    // Load value mappings from script tag in DOM
    this.valueMap = this.loadValueMappings();
  }
  
  /**
   * Load value mappings from data-quiz-value-mappings script tag
   */
  loadValueMappings() {
    const scriptTag = document.querySelector('script[data-quiz-value-mappings]');
    if (!scriptTag) {
      console.warn('Quiz value mappings script tag not found, using fallback');
      return this.getFallbackValueMap();
    }
    
    try {
      const mappings = JSON.parse(scriptTag.textContent);
      const valueMap = {};
      
      // Build mapping for glove_size
      if (mappings.glove_size && Array.isArray(mappings.glove_size)) {
        mappings.glove_size.forEach(item => {
          if (item.value && item.name) {
            valueMap[item.value.toLowerCase()] = item.name;
          }
        });
      }
      
      // Build mapping for hand_size
      if (mappings.hand_size && Array.isArray(mappings.hand_size)) {
        mappings.hand_size.forEach(item => {
          if (item.value && item.name) {
            valueMap[item.value.toLowerCase()] = item.name;
          }
        });
      }
      
      // Build mapping for glove_style
      if (mappings.glove_style && Array.isArray(mappings.glove_style)) {
        mappings.glove_style.forEach(item => {
          if (item.value && item.name) {
            valueMap[item.value.toLowerCase()] = item.name;
          }
        });
      }
      
      // Static mappings for other fields (moisture, feel, texture)
      valueMap['yes'] = 'Yes';
      valueMap['no'] = 'No';
      valueMap['soft'] = 'Soft';
      valueMap['firm'] = 'Firm';
      valueMap['in-between'] = 'In Between';
      valueMap['smooth'] = 'Smooth';
      valueMap['rough'] = 'Rough';
      
      return valueMap;
    } catch (error) {
      console.error('Error parsing quiz value mappings:', error);
      return this.getFallbackValueMap();
    }
  }
  
  /**
   * Fallback value map if script tag is not available
   */
  getFallbackValueMap() {
    return {
      // Static mappings for other fields
      'yes': 'Yes',
      'no': 'No',
      'soft': 'Soft',
      'firm': 'Firm',
      'in-between': 'In Between',
      'smooth': 'Smooth',
      'rough': 'Rough'
    };
  }
  
  /**
   * Convert quiz value to product metafield value
   */
  mapValue(quizValue) {
    if (!quizValue) return null;
    return this.valueMap[quizValue.toLowerCase()] || quizValue;
  }

  /**
   * Build combination string from quiz answers
   * Format: [hand_size],[moisture],[feel],[texture] OR [glove_style],[glove_size],[moisture],[feel],[texture]
   * If hand_size is selected, glove_style is NOT included
   */
  buildCombinationString() {
    const parts = [];
    
    // If hand_size is selected, use hand_size path (without glove_style)
    if (this.answers.hand_size) {
      const handSizeMapped = this.mapValue(this.answers.hand_size);
      parts.push(handSizeMapped || this.answers.hand_size);
    } 
    // If glove_size is selected (and no hand_size), include glove_style and glove_size
    else if (this.answers.glove_size) {
      if (this.answers.glove_style) {
        const gloveStyleMapped = this.mapValue(this.answers.glove_style);
        parts.push(gloveStyleMapped || this.answers.glove_style);
      }
      const gloveSizeMapped = this.mapValue(this.answers.glove_size);
      parts.push(gloveSizeMapped || this.answers.glove_size);
    }
    
    // Add moisture_control if selected
    if (this.answers.moisture) {
      const moistureMapped = this.mapValue(this.answers.moisture);
      parts.push(moistureMapped || this.answers.moisture);
    }
    
    // Add grip_texture (feel) if selected
    if (this.answers.feel) {
      const feelMapped = this.mapValue(this.answers.feel);
      parts.push(feelMapped || this.answers.feel);
    }
    
    // Add texture_type if selected
    if (this.answers.texture) {
      const textureMapped = this.mapValue(this.answers.texture);
      parts.push(textureMapped || this.answers.texture);
    }
    
    return parts.join(',');
  }

  /**
   * Build GraphQL query for filtering products by specific metafield
   * @param {string} metafieldKey - The metafield key to filter by (quiz_combination_options or quiz_combination_options_second_part)
   */
  buildGraphQLQuery(metafieldKey) {
    return `
      query CollectionProducts($handle: String!, $combinationValue: String!) {
        collection(handle: $handle) {
          id
          handle
          title
          products(
            first: 50
            filters: {productMetafield: {namespace: "custom", key: "${metafieldKey}", value: $combinationValue}}
          ) {
            nodes {
              id
              title
              handle
              descriptionHtml
              metafield(key: "short_description", namespace: "custom") {
                value
              }
              quiz_combination_options: metafield(key: "quiz_combination_options", namespace: "custom") {
                value
              }
              quiz_combination_options_second_part: metafield(key: "quiz_combination_options_second_part", namespace: "custom") {
                value
              }
              quiz_recommended_grip_size_and_glove_size:metafield(key: "quiz_recommended_grip_size_for_hand_size", namespace: "quiz") {
                id
                value
                references(first: 250) {
                  nodes {
                    ... on Metaobject {
                      id
                      fields {
                        value
                        key
                      }
                    }
                  }
                }
              }
              quiz_recommended_grip_size_and_hand_size:metafield(key: "quiz_recommended_grip_size_for_glove_size", namespace: "quiz") {
                id
                value
                references(first: 250) {
                  nodes {
                    ... on Metaobject {
                      id
                      fields {
                        value
                        key
                      }
                    }
                  }
                }
              }
              featuredImage {
                url
                altText
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
                maxVariantPrice {
                  amount
                  currencyCode
                }
              }
              variants(first: 50) {
                nodes {
                  id
                  title
                  availableForSale
                  image {
                    url
                    altText
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
              options(first: 100) {
                name
                optionValues {
                  swatch {
                    image {
                      previewImage {
                        src
                        originalSrc
                      }
                      alt
                    }
                  }
                  name
                }
              }
            }
          }
        }
      }
    `;
  }


  /**
   * Execute GraphQL query to Shopify Storefront API
   */
  async executeGraphQLQuery(query, variables = {}) {
    try {
      const token = '6bd8c02554835a9221800eeba124ccf7';
      
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ 
          query,
          variables: {
            handle: this.collectionHandle,
            ...variables
          }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        
        if (response.status === 401) {
          throw new Error(`401 Unauthorized: Storefront Access Token is invalid or missing required permissions. Check your Private App settings.`);
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.errors) {
        throw new Error(`GraphQL errors: ${data.errors.map(e => e.message).join(', ')}`);
      }
      
      return data.data;
    } catch (error) {
      console.error('GraphQL query failed:', error);
      throw error;
    }
  }

  /**
   * Fetch filtered products from collection using API-level filtering
   * Makes two parallel requests for quiz_combination_options and quiz_combination_options_second_part
   * Merges results and removes duplicates
   */
  async fetchFilteredProducts() {
    try {
      // Build combination string from quiz answers
      const combinationString = this.buildCombinationString();
      
      // Build two GraphQL queries for different metafields
      const query1 = this.buildGraphQLQuery('quiz_combination_options');
      const query2 = this.buildGraphQLQuery('quiz_combination_options_second_part');
      
      
      // Execute both queries in parallel
      const [data1, data2] = await Promise.all([
        this.executeGraphQLQuery(query1, { combinationValue: combinationString }),
        this.executeGraphQLQuery(query2, { combinationValue: combinationString })
      ]);
      
      // Get products from both queries
      const products1 = data1?.collection?.products?.nodes || [];
      const products2 = data2?.collection?.products?.nodes || [];
      
      
      // Merge products and remove duplicates by product.id
      const productMap = new Map();
      
      // Add products from first query
      products1.forEach(product => {
        productMap.set(product.id, product);
      });
      
      // Add products from second query (only if not already present)
      products2.forEach(product => {
        if (!productMap.has(product.id)) {
          productMap.set(product.id, product);
        }
      });
      
      // Convert map back to array
      const products = Array.from(productMap.values());
      
      // Store collection handle
      const collectionHandle = data1?.collection?.handle || data2?.collection?.handle || null;
      
      // Add collection handle and match info to each product
      products.forEach(product => {
        product.collectionHandle = collectionHandle;
        // Since products are already filtered by API, they all match
        product.matchScore = 100;
        product.matchCount = 1;
        product.totalCriteria = 1;
        
        // Determine which field matched
        const matchedField = product.quiz_combination_options?.value === combinationString 
          ? 'quiz_combination_options' 
          : 'quiz_combination_options_second_part';
        
        product.matchedFields = [matchedField];
        product.mismatchedFields = [];
        product.expectedCombination = combinationString;
        product.productCombination = product.quiz_combination_options?.value || product.quiz_combination_options_second_part?.value || '';
      });
      
      return products;
      
    } catch (error) {
      console.error('Error fetching filtered products:', error);
      return [];
    }
  }

  /**
   * Score products based on quiz combination match
   * Products must match the exact combination string in either quiz_combination_options or quiz_combination_options_second_part
   */
  scoreProducts(products) {
    // Build the combination string from quiz answers
    const expectedCombination = this.buildCombinationString();
    
    return products.map(product => {
      const productCombination1 = product.quiz_combination_options?.value || '';
      const productCombination2 = product.quiz_combination_options_second_part?.value || '';
      
      // Check if either field matches expected combination
      const isMatch1 = productCombination1.toLowerCase().trim() === expectedCombination.toLowerCase().trim();
      const isMatch2 = productCombination2.toLowerCase().trim() === expectedCombination.toLowerCase().trim();
      const isMatch = isMatch1 || isMatch2;
      
      const matchScore = isMatch ? 100 : 0;
      const matchCount = isMatch ? 1 : 0;
      const totalCriteria = 1;
      
      // Determine which field matched
      let matchedFields = [];
      let productCombination = '';
      if (isMatch1) {
        matchedFields.push('quiz_combination_options');
        productCombination = productCombination1;
      }
      if (isMatch2) {
        matchedFields.push('quiz_combination_options_second_part');
        productCombination = productCombination2;
      }
      
      return {
        ...product,
        matchScore,
        matchCount,
        totalCriteria,
        matchedFields,
        mismatchedFields: isMatch ? [] : [`Expected "${expectedCombination}", got "${productCombination1}" / "${productCombination2}"`],
        expectedCombination,
        productCombination
      };
    });
  }


  /**
   * Get top product recommendation (returns the single best match)
   */
  async getTopRecommendation() {
    const filteredProducts = await this.fetchFilteredProducts();
    
    if (filteredProducts.length === 0) {
      return null;
    }
    
    // Sort by match score (highest first), then by number of matches
    filteredProducts.sort((a, b) => {
      if (b.matchScore !== a.matchScore) {
        return b.matchScore - a.matchScore;
      }
      return b.matchCount - a.matchCount;
    });
    
    // Return the top matching product
    const topProduct = filteredProducts[0];
    
    return this.formatProduct(topProduct, true);
  }

  /**
   * Get all recommended products (for debugging or alternative use)
   */
  async getAllRecommendations(limit = 10) {
    const filteredProducts = await this.fetchFilteredProducts();
    
    // Sort by match score and match count
    filteredProducts.sort((a, b) => {
      if (b.matchScore !== a.matchScore) {
        return b.matchScore - a.matchScore;
      }
      return b.matchCount - a.matchCount;
    });
    
    return filteredProducts.slice(0, limit).map(p => this.formatProduct(p));
  }

  /**
   * Format product data for display
   */
  formatProduct(product, includeCollection = false) {
    if (!product) return null;

    // Extract color option and its swatches
    const colorOption = product.options?.find(opt => 
      opt.name && (opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour')
    );
    
    // Build swatch map: color value -> swatch image
    const swatchMap = {};
    if (colorOption && colorOption.optionValues) {
      colorOption.optionValues.forEach(optionValue => {
        const colorName = optionValue.name;
        const swatchImage = optionValue.swatch?.image?.previewImage?.originalSrc || 
                           optionValue.swatch?.image?.previewImage?.src;
        
        if (swatchImage) {
          swatchMap[colorName] = swatchImage;
        }
      });
    }
    
    // Get unique color variants (no duplicates)
    const seenColors = new Set();
    const uniqueVariants = [];
    
    (product.variants?.nodes || []).forEach(variant => {
      const colorOption = variant.selectedOptions?.find(opt => 
        opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour'
      );
      
      const colorValue = colorOption?.value;
      
      // Only add if we haven't seen this color yet
      if (colorValue && !seenColors.has(colorValue)) {
        seenColors.add(colorValue);
        
        const swatchImage = swatchMap[colorValue] || null;
        
        uniqueVariants.push({
          id: variant.id,
          title: variant.title,
          available: variant.availableForSale,
          image: variant.image?.url || product.featuredImage?.url || '',
          color: colorValue,
          swatchImage: swatchImage,
          selectedOptions: variant.selectedOptions || []
        });
      }
    });

    // Prefer custom.short_description metafield; fall back to product descriptionHtml
    let description = product.descriptionHtml || '';
    let descriptionHTML = product.metafield?.value || '';

    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      url: `/products/${product.handle}`,
      description: description,
      descriptionHTML: descriptionHTML,
      quiz_recommended_grip_size_and_glove_size: product.quiz_recommended_grip_size_and_glove_size,
      quiz_recommended_grip_size_and_hand_size: product.quiz_recommended_grip_size_and_hand_size,
      featured_image: product.featuredImage?.url || '',
      price: product.priceRange?.minVariantPrice?.amount || 0,
      compare_at_price: null, // Not available in Storefront API
      available: product.availableForSale,
      variants: uniqueVariants,
      colorOption: colorOption,
      tags: [], // Not available in Storefront API
      matchScore: product.matchScore || 0,
      matchedFields: product.matchedFields || [],
      matchCount: product.matchCount || 0,
      totalCriteria: product.totalCriteria || 0,
      collectionHandle: includeCollection && product.collectionHandle ? product.collectionHandle : null
    };
  }

  /**
   * Calculate size recommendation based on answers
   */
  calculateSizeRecommendation() {
    if (this.answers.hand_size) {
      const handSize = this.answers.hand_size;
      if (handSize === 'under-5') return 'Undersize';
      if (handSize === '5-6.5') return 'Standard';
      if (handSize === '6.6-7.5') return 'Midsize';
      if (handSize === '7.6-9' || handSize === 'over-9') return 'Oversize';
    }
    
    if (this.answers.glove_size) {
      const gloveSize = this.answers.glove_size;
      if (gloveSize === 'small') return 'Undersize';
      if (gloveSize === 'medium') return 'Standard';
      if (gloveSize === 'medium-large' || gloveSize === 'large') return 'Midsize';
      if (gloveSize === 'x-large' || gloveSize === 'xx-large') return 'Oversize';
    }
    
    return 'Standard';
  }

  /**
   * Truncate text to specified length
   */
  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substr(0, maxLength).trim() + '...';
  }

}

// Make QuizFilter available globally
window.QuizFilter = QuizFilter;